import { FC, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import styled from "@emotion/styled";
import { Button } from "@/components/button";
import { atlasRoutes } from "@/app/router/routes";
import { SharedItemInviteTable } from "./shared-item-invite-table";
import {
  SharedItemInviteSearchFilters,
  SharedItemInviteSearchForm,
} from "./shared-item-invite-search-form";
import {
  SearchSharedItemInvitesResponse,
  SharedItemInviteSummary,
} from "@/pages/utilities/shared-item-invites/shared/types";
import { api } from "@/modules/api";
import { Flex, Text } from "@radix-ui/themes";
import { usePrimarySpaceAccountId } from "@/pages/utilities/shared-item-invites/shared/use-primary-space-account-id";

const PAGE_SIZE = 50;

const readBooleanParam = (value: string | null, fallback: boolean) => {
  if (value === null) return fallback;
  return value === "true";
};

const buildQueryParams = (filters: SharedItemInviteSearchFilters) => {
  const params = new URLSearchParams();
  if (filters.inviterSpaceAccountId) params.set("inviter", filters.inviterSpaceAccountId);
  if (filters.sharedItemId) params.set("item", filters.sharedItemId);
  if (filters.sharedItemKind) params.set("kind", filters.sharedItemKind);
  if (filters.status) params.set("status", filters.status);
  if (filters.includeRevoked !== undefined)
    params.set("include_revoked", String(filters.includeRevoked));
  return params;
};

export const SharedItemInvitesRootPage: FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { primarySpaceAccountId, isLoading: isPrimaryLoading } = usePrimarySpaceAccountId();

  const filters: SharedItemInviteSearchFilters = {
    inviterSpaceAccountId: searchParams.get("inviter") ?? undefined,
    sharedItemId: searchParams.get("item") ?? undefined,
    sharedItemKind: searchParams.get("kind") ?? undefined,
    status: searchParams.get("status") ?? undefined,
    includeRevoked: readBooleanParam(searchParams.get("include_revoked"), true),
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["shared-item-invites", filters],
    queryFn: async () => {
      const response = await api.post("/internal/v1/shared-item-invites/search", {
        body: {
          limit: PAGE_SIZE,
          offset: 0,
          filter_by_inviter_space_account_id: filters.inviterSpaceAccountId,
          filter_by_shared_item_id: filters.sharedItemId,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          filter_by_shared_item_kind: filters.sharedItemKind as any,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          filter_by_status: filters.status as any,
          include_revoked: filters.includeRevoked,
        },
      });
      return response.data as SearchSharedItemInvitesResponse;
    },
  });

  const invites = useMemo<SharedItemInviteSummary[]>(() => data?.results ?? [], [data]);

  const total = data?.total ?? 0;

  const handleSearch = (updatedFilters: SharedItemInviteSearchFilters) => {
    const params = buildQueryParams(updatedFilters);
    setSearchParams(params);
  };

  const handleViewInvite = (inviteId: string) => {
    navigate(atlasRoutes.sharedItemInvitesView({ params: { inviteId } }).path);
  };

  return (
    <Container>
      <Header>
        <div>
          <h1>Shared Item Invites</h1>
          <Text color="gray" size="2">
            Filter and inspect invites created via the new sharing pipeline.
          </Text>
        </div>
        <Button onClick={() => navigate(atlasRoutes.sharedItemInvitesCreate({}).path)}>
          Create Invite
        </Button>
      </Header>

      <SharedItemInviteSearchForm
        initialFilters={filters}
        onSearch={handleSearch}
        primarySpaceAccountId={primarySpaceAccountId}
        isPrimaryLoading={isPrimaryLoading}
      />

      {isLoading && <div>Loading invites…</div>}
      {isError && (
        <div>Error loading invites: {error instanceof Error ? error.message : "Unknown error"}</div>
      )}

      {!isLoading && invites.length === 0 && (
        <Text size="2" color="gray">
          No invites found. Adjust your filters to see results.
        </Text>
      )}

      {invites.length > 0 && (
        <Flex direction="column" gap="2">
          <Text size="2" color="gray">
            Showing {invites.length} of {total} invites
          </Text>
          <SharedItemInviteTable
            invites={invites}
            pageSize={PAGE_SIZE}
            onViewInvite={handleViewInvite}
          />
        </Flex>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Header = styled(Flex)`
  justify-content: space-between;
  align-items: center;
`;
